import React from "react";

const Quick4Review = React.memo(({
  quick4ReviewStep,
  setQuick4ReviewStep,
  quick4ReviewWords,
  quick4CurrentIndex,
  setQuick4CurrentIndex,
  quick4Results,
  setQuick4Results,
  quick4SpellingInput,
  setQuick4SpellingInput,
  quick4SpellingResult,
  setQuick4SpellingResult,
  quick4DifficultWords,
  setQuick4DifficultWords,
  quick4DifficultIndex,
  setQuick4DifficultIndex,
  quick4DifficultDirection,
  setQuick4DifficultDirection,
  showVietnameseMeaning,
  setShowVietnameseMeaning,
  playVocabAudio,
  playSentenceAudio,
  playGoogleTTS,
  stopAllAudio,
  calculateNextReview,
  onUpdateVocabulary,
  resetQuick4Review,
  setFlashcardModalStep,
  startQuick4Review
}) => {
  const handleQuick4Next = () => {
    const currentStep = quick4ReviewStep;
    const currentIndex = quick4CurrentIndex;
    const totalWords = quick4ReviewWords.length;

    stopAllAudio();
    setQuick4SpellingInput("");
    setQuick4SpellingResult(null);

    if (currentStep === "preview") {
      setShowVietnameseMeaning(false);
    }

    if (currentStep === "preview") {
      if (currentIndex < totalWords - 1) {
        setQuick4CurrentIndex(currentIndex + 1);
      } else {
        setQuick4ReviewStep("jp-vi");
        setQuick4CurrentIndex(0);
      }
    } else if (currentStep === "jp-vi") {
      if (currentIndex < totalWords - 1) {
        setQuick4CurrentIndex(currentIndex + 1);
      } else {
        setQuick4ReviewStep("vi-jp");
        setQuick4CurrentIndex(0);
      }
    } else if (currentStep === "vi-jp") {
      if (currentIndex < totalWords - 1) {
        setQuick4CurrentIndex(currentIndex + 1);
      } else {
        if (quick4DifficultWords.length > 0) {
          setQuick4ReviewStep("review-difficult");
          setQuick4DifficultIndex(0);
          setQuick4DifficultDirection("jp-vi");
        } else {
          setQuick4ReviewStep("summary");
        }
      }
    }
  };

  const handleQuick4SpellingSubmit = () => {
    const currentWord = quick4ReviewWords[quick4CurrentIndex];
    const isJpVi = quick4ReviewStep === "jp-vi";
    const targetWord = isJpVi ? currentWord.wordVi : currentWord.wordJp;
    const userInput = quick4SpellingInput.trim().toLowerCase();
    const correct = userInput === targetWord.toLowerCase();

    setQuick4SpellingResult({
      correct,
      userInput: quick4SpellingInput.trim(),
      correctAnswer: targetWord,
    });

    if (currentWord.audioVocab) {
      setTimeout(() => playVocabAudio(currentWord.audioVocab, currentWord.wordJp), 500);
    } else {
      setTimeout(() => playGoogleTTS(currentWord.wordJp), 500);
    }
  };

  const handleQuick4DifficultyChoice = (difficulty) => {
    const currentIndex = quick4ReviewStep === "review-difficult" ? quick4DifficultIndex : quick4CurrentIndex;
    const isJpVi = quick4ReviewStep === "jp-vi" || (quick4ReviewStep === "review-difficult" && quick4DifficultDirection === "jp-vi");
    const currentWord = quick4ReviewStep === "review-difficult" ? quick4DifficultWords[quick4DifficultIndex].word : quick4ReviewWords[quick4CurrentIndex];

    if (quick4ReviewStep !== "review-difficult") {
      setQuick4Results((prev) => {
        const newResults = [...prev];
        if (isJpVi) {
          newResults[currentIndex].jpVi = difficulty;
        } else {
          newResults[currentIndex].viJp = difficulty;
        }
        return newResults;
      });

      if (difficulty === "again" || difficulty === "hard") {
        const difficultWordInfo = {
          word: currentWord,
          step: quick4ReviewStep,
          originalIndex: currentIndex,
          difficulty: difficulty,
        };

        setQuick4DifficultWords((prev) => {
          const exists = prev.find((w) => w.word.id === currentWord.id && w.step === quick4ReviewStep);
          if (!exists) {
            return [...prev, difficultWordInfo];
          }
          return prev;
        });
      }
    }

    const reviewResult = calculateNextReview(difficulty, currentWord.step || 0);
    const updatedWord = {
      ...currentWord,
      reviewCount: currentWord.reviewCount + 1,
      correctCount: difficulty === "easy" || difficulty === "medium" ? currentWord.correctCount + 1 : currentWord.correctCount,
      nextReview: reviewResult.nextReview,
      step: reviewResult.step,
      intervalHours: reviewResult.intervalHours,
      difficulty: difficulty,
      lastReviewed: new Date(),
    };

    onUpdateVocabulary(updatedWord);

    setTimeout(() => {
      if (quick4ReviewStep === "review-difficult") {
        handleQuick4DifficultNext();
      } else {
        handleQuick4Next();
      }
    }, 300);
  };

  const handleQuick4DifficultNext = () => {
    const currentDirection = quick4DifficultDirection;
    const currentIndex = quick4DifficultIndex;
    const totalDifficultWords = quick4DifficultWords.length;

    stopAllAudio();
    setQuick4SpellingInput("");
    setQuick4SpellingResult(null);

    if (currentDirection === "jp-vi") {
      setQuick4DifficultDirection("vi-jp");
    } else {
      if (currentIndex < totalDifficultWords - 1) {
        setQuick4DifficultIndex(currentIndex + 1);
        setQuick4DifficultDirection("jp-vi");
      } else {
        setQuick4ReviewStep("summary");
      }
    }
  };

  const handleQuick4DifficultSpellingSubmit = () => {
    const currentWord = quick4DifficultWords[quick4DifficultIndex].word;
    const isJpVi = quick4DifficultDirection === "jp-vi";
    const targetWord = isJpVi ? currentWord.wordVi : currentWord.wordJp;
    const userInput = quick4SpellingInput.trim().toLowerCase();
    const correct = userInput === targetWord.toLowerCase();

    setQuick4SpellingResult({
      correct,
      userInput: quick4SpellingInput.trim(),
      correctAnswer: targetWord,
    });

    if (currentWord.audioVocab) {
      setTimeout(() => playVocabAudio(currentWord.audioVocab, currentWord.wordJp), 500);
    } else {
      setTimeout(() => playGoogleTTS(currentWord.wordJp), 500);
    }
  };

  const finishQuick4Review = () => {
    const completedWords = quick4ReviewWords.length;
    const totalTests = completedWords * 2;
    const easyCount = quick4Results.flat().filter((r) => r === "easy").length;
    const mediumCount = quick4Results.flat().filter((r) => r === "medium").length;
    const hardCount = quick4Results.flat().filter((r) => r === "hard").length;
    const againCount = quick4Results.flat().filter((r) => r === "again").length;
    const difficultWordsCount = quick4DifficultWords.length;

    let message = `🎉 Hoàn thành ôn tập nhanh!\n\n📊 Kết quả:\n• Dễ: ${easyCount}/${totalTests}\n• Bình thường: ${mediumCount}/${totalTests}\n• Khó: ${hardCount}/${totalTests}\n• Quên: ${againCount}/${totalTests}`;

    if (difficultWordsCount > 0) {
      message += `\n\n💪 Bạn đã ôn lại ${difficultWordsCount} từ khó/quên. Tuyệt vời!`;
    }

    message += `\n\nTiếp tục luyện tập để cải thiện!`;

    resetQuick4Review();
    setFlashcardModalStep("options");
    alert(message);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-end gap-4 text-sm pt-14">
          <span className="px-4 py-2 bg-black/60 rounded-xl text-cyan-300 font-mono font-bold">
            {quick4ReviewStep === "review-difficult"
              ? `[${quick4DifficultIndex + 1}/${quick4DifficultWords.length}_DIFFICULT]`
              : `[${quick4CurrentIndex + 1}/${quick4ReviewWords.length}_UNITS]`}
          </span>
          <span className="text-cyan-400">{"•"}</span>
          <span className="text-purple-300 font-mono font-bold">
            {quick4ReviewStep === "preview" && "[PREVIEW_MODE]"}
            {quick4ReviewStep === "jp-vi" && "[JP→VI_TEST]"}
            {quick4ReviewStep === "vi-jp" && "[VI→JP_TEST]"}
            {quick4ReviewStep === "review-difficult" && `[RETRY_DIFFICULT_${quick4DifficultDirection === "jp-vi" ? "JP→VI" : "VI→JP"}]`}
            {quick4ReviewStep === "summary" && "[SUMMARY_REPORT]"}
          </span>
        </div>
      </div>

      {/* Preview Step */}
      {quick4ReviewStep === "preview" && quick4ReviewWords[quick4CurrentIndex] && (
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-6">
          <div className="text-center space-y-4">
            <div className="space-y-3">
              <div className="bg-black/80 border border-cyan-400/30 rounded-lg p-4 backdrop-blur-sm shadow-neon-cyan">
                <div className="text-2xl font-mono font-bold mb-2 text-cyan-300">
                  {quick4ReviewWords[quick4CurrentIndex].wordJp}
                </div>
                <div className="flex flex-col items-center justify-center gap-3">
                  <div className="text-lg text-green-300 font-mono">
                    {showVietnameseMeaning ? quick4ReviewWords[quick4CurrentIndex].wordVi : "___________"}
                  </div>
                  <button
                    onClick={() => setShowVietnameseMeaning(!showVietnameseMeaning)}
                    className="p-2 bg-black/60 border border-green-400/50 rounded-lg hover:border-green-400 hover:shadow-neon-green transition-all duration-300 text-green-300 hover:text-green-100 transform hover:scale-105"
                  >
                    {showVietnameseMeaning ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="size-4">
  <path fill-rule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l10.5 10.5a.75.75 0 1 0 1.06-1.06l-1.322-1.323a7.012 7.012 0 0 0 2.16-3.11.87.87 0 0 0 0-.567A7.003 7.003 0 0 0 4.82 3.76l-1.54-1.54Zm3.196 3.195 1.135 1.136A1.502 1.502 0 0 1 9.45 8.389l1.136 1.135a3 3 0 0 0-4.109-4.109Z" clip-rule="evenodd" />
  <path d="m7.812 10.994 1.816 1.816A7.003 7.003 0 0 1 1.38 8.28a.87.87 0 0 1 0-.566 6.985 6.985 0 0 1 1.113-2.039l2.513 2.513a3 3 0 0 0 2.806 2.806Z" />
</svg>
 : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="size-4">
 <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
 <path fill-rule="evenodd" d="M1.38 8.28a.87.87 0 0 1 0-.566 7.003 7.003 0 0 1 13.238.006.87.87 0 0 1 0 .566A7.003 7.003 0 0 1 1.379 8.28ZM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" clip-rule="evenodd" />
</svg>
}
                  </button>
                </div>
              </div>

              {quick4ReviewWords[quick4CurrentIndex].exampleSentence && (
                <div className="bg-black/70 rounded-lg p-3 backdrop-blur-sm">
                  <div className="text-sm text-purple-300 mb-1 font-mono">EXAMPLE CONTEXT:</div>
                  <div className="text-sm italic text-pink-300 font-mono">
                    {quick4ReviewWords[quick4CurrentIndex].exampleSentence}
                  </div>
                </div>
              )}

              {/* Image Display for Preview */}
              {quick4ReviewWords[quick4CurrentIndex].imageUrl && (
                <div className="bg-black/40 border border-orange-400/30 rounded-xl p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 text-orange-400">
                      <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-mono font-bold text-orange-300">VISUAL REFERENCE</span>
                  </div>
                  <div className="flex justify-center">
                    <img
                      src={quick4ReviewWords[quick4CurrentIndex].imageUrl}
                      alt={`Visual reference for ${quick4ReviewWords[quick4CurrentIndex].wordJp}`}
                      className="max-w-full max-h-32 sm:max-h-40 rounded-lg border border-orange-400/40 object-contain cursor-pointer hover:border-orange-400 transition-all duration-300 hover:shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        e.target.nextSibling.style.display = 'none';
                      }}
                      onClick={(e) => {
                        window.open(quick4ReviewWords[quick4CurrentIndex].imageUrl, '_blank');
                      }}
                      title="Click to view full size"
                    />
                    <div 
                      className="flex items-center justify-center text-red-300 text-xs font-mono p-3 border border-red-400/50 rounded-lg bg-black/60"
                      style={{ display: 'none' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 mr-2">
                        <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.3 1.3 0 0 1-1.299 2H2.804a1.3 1.3 0 0 1-1.3-2l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                      </svg>
                      Failed to load image
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Audio Controls */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  const word = quick4ReviewWords[quick4CurrentIndex];
                  if (word.audioVocab) {
                    playVocabAudio(word.audioVocab, word.wordJp);
                  } else {
                    playGoogleTTS(word.wordJp);
                  }
                }}
                className="p-3 bg-black/80 border border-cyan-400/50 rounded-lg hover:border-cyan-400 hover:shadow-neon-cyan-intense transition-all duration-300 backdrop-blur-sm text-cyan-300 hover:text-cyan-100 transform hover:scale-105"
                title="[PLAY_VOCAB_AUDIO]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="size-4">
  <path d="M7.557 2.066A.75.75 0 0 1 8 2.75v10.5a.75.75 0 0 1-1.248.56L3.59 11H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.59l3.162-2.81a.75.75 0 0 1 .805-.124ZM12.95 3.05a.75.75 0 1 0-1.06 1.06 5.5 5.5 0 0 1 0 7.78.75.75 0 1 0 1.06 1.06 7 7 0 0 0 0-9.9Z" />
  <path d="M10.828 5.172a.75.75 0 1 0-1.06 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 1 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
</svg>

              </button>

              {quick4ReviewWords[quick4CurrentIndex].exampleSentence && (
                <button
                  onClick={() => {
                    const word = quick4ReviewWords[quick4CurrentIndex];
                    if (word.audioSentence) {
                      playSentenceAudio(word.audioSentence, word.sentenceStartTime || 0, word.sentenceEndTime || 0);
                    } else {
                      playGoogleTTS(word.exampleSentence);
                    }
                  }}
                  className="p-3 bg-black/80 border border-purple-400/50 rounded-lg hover:border-purple-400 hover:shadow-neon-purple-intense transition-all duration-300 backdrop-blur-sm text-purple-300 hover:text-purple-100 transform hover:scale-105"
                  title="[PLAY_EXAMPLE_AUDIO]"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C13.1 2 14 2.9 14 4V20C14 21.1 13.1 22 12 22C10.9 22 10 21.1 10 20V4C10 2.9 10.9 2 12 2ZM19 10V14C19 15.1 18.1 16 17 16H16V10H17C18.1 10 19 10.9 19 14ZM8 10V16H7C5.9 16 5 15.1 5 14V10C5 10.9 5.9 10 7 10H8Z" />
                  </svg>
                </button>
              )}
            </div>

            <button
              onClick={handleQuick4Next}
              className="mt-4 px-6 py-3 bg-black/80 border border-cyan-400/50 rounded-lg font-mono font-bold text-cyan-300 hover:border-cyan-400 hover:shadow-neon-cyan-intense hover:text-cyan-100 transition-all duration-300 transform hover:scale-105"
            >
              {quick4CurrentIndex < quick4ReviewWords.length - 1 ? "TIẾP TỤC" : "BẮT ĐẦU THỬ NGHIỆM"}
            </button>
          </div>
        </div>
      )}

      {/* Spelling Test Steps (jp-vi and vi-jp) */}
      {(quick4ReviewStep === "jp-vi" || quick4ReviewStep === "vi-jp") && quick4ReviewWords[quick4CurrentIndex] && (
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-6">
          <div className="text-center space-y-4">
            <div className="bg-black/80 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-xl font-mono font-bold mb-4 text-purple-300">
                {quick4ReviewStep === "jp-vi" ? quick4ReviewWords[quick4CurrentIndex].wordJp : quick4ReviewWords[quick4CurrentIndex].wordVi}
              </div>

              {/* Image Display for Testing */}
              {quick4ReviewWords[quick4CurrentIndex].imageUrl && (
                <div className="mb-4 bg-black/40 border border-orange-400/30 rounded-xl p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 text-orange-400">
                      <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-mono font-bold text-orange-300">GỢI Ý HÌNH ẢNH</span>
                  </div>
                  <div className="flex justify-center">
                    <img
                      src={quick4ReviewWords[quick4CurrentIndex].imageUrl}
                      alt={`Gợi ý hình ảnh cho ${quick4ReviewWords[quick4CurrentIndex].wordJp}`}
                      className="max-w-full max-h-28 sm:max-h-32 rounded-lg border border-orange-400/40 object-contain cursor-pointer hover:border-orange-400 transition-all duration-300 hover:shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        e.target.nextSibling.style.display = 'none';
                      }}
                      onClick={(e) => {
                        window.open(quick4ReviewWords[quick4CurrentIndex].imageUrl, '_blank');
                      }}
                      title="Click to view full size"
                    />
                    <div 
                      className="flex items-center justify-center text-red-300 text-xs font-mono p-3 border border-red-400/50 rounded-lg bg-black/60"
                      style={{ display: 'none' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 mr-2">
                        <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.3 1.3 0 0 1-1.299 2H2.804a1.3 1.3 0 0 1-1.3-2l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                      </svg>
                      Không thể tải hình ảnh
                    </div>
                  </div>
                </div>
              )}

              {!quick4SpellingResult && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={quick4SpellingInput}
                    onChange={(e) => setQuick4SpellingInput(e.target.value)}
                    placeholder={`Nhập bản dịch ${quick4ReviewStep === "jp-vi" ? "tiếng Việt" : "tiếng Nhật"}`}
                    className="w-full px-4 py-3 rounded-lg bg-black/80 border border-cyan-400/50 text-cyan-300 text-center text-lg font-mono placeholder-cyan-500/50 focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && quick4SpellingInput.trim()) {
                        handleQuick4SpellingSubmit();
                      }
                    }}
                    autoFocus
                  />
                  <button
                    onClick={handleQuick4SpellingSubmit}
                    disabled={!quick4SpellingInput.trim()}
                    className="px-6 py-3 bg-black/80 border border-purple-400/50 rounded-lg font-mono font-bold text-purple-300 hover:border-purple-400 hover:shadow-neon-purple-intense hover:text-purple-100 transition-all duration-300 disabled:opacity-50 transform hover:scale-105"
                  >
                    KIỂM TRA ĐÁP ÁN
                  </button>
                </div>
              )}

              {quick4SpellingResult && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border font-mono ${quick4SpellingResult.correct ? "bg-green-900/60 border-green-400/50 text-green-300 shadow-neon-green" : "bg-red-900/60 border-red-400/50 text-red-300 shadow-neon-red"}`}>
                    <div className="font-bold text-lg mb-2">
                      {quick4SpellingResult.correct ? "[THÀNH CÔNG] ĐÚNG!" : "[LỖI] SAI!"}
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="font-bold">
                        <span className="">NHẬP VÀO: </span>
                        <span className="font-bold">{quick4SpellingResult.userInput}</span>
                      </div>
                      <div className="font-bold">
                        ĐÁP ÁN: <span className="font-bold text-green-300">{quick4SpellingResult.correctAnswer}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => handleQuick4DifficultyChoice("again")} className="p-3 bg-black/80 border border-red-400/50 rounded-lg font-mono font-bold text-red-300 hover:border-red-400 hover:shadow-neon-red-intense hover:text-red-100 transition-all duration-300 transform hover:scale-105">
                        QUÊN
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("hard")} className="p-3 bg-black/80 border border-orange-400/50 rounded-lg font-mono font-bold text-orange-300 hover:border-orange-400 hover:shadow-neon-orange-intense hover:text-orange-100 transition-all duration-300 transform hover:scale-105">
                        KHÓ
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("medium")} className="p-3 bg-black/80 border border-blue-400/50 rounded-lg font-mono font-bold text-blue-300 hover:border-blue-400 hover:shadow-neon-blue-intense hover:text-blue-100 transition-all duration-300 transform hover:scale-105">
                        BÌNH THƯỜNG
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("easy")} className="p-3 bg-black/80 border border-green-400/50 rounded-lg font-mono font-bold text-green-300 hover:border-green-400 hover:shadow-neon-green-intense hover:text-green-100 transition-all duration-300 transform hover:scale-105">
                        DỄ
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Difficult Words Step */}
      {quick4ReviewStep === "review-difficult" && quick4DifficultWords.length > 0 && (
        <div className="bg-black/60 backdrop-blur-sm border border-orange-400/50 rounded-xl p-6 shadow-neon-orange">
          <div className="text-center space-y-4">
            <h3 className="text-lg font-mono font-bold text-orange-300">
              ÔN LẠI KHÓ {quick4DifficultDirection === "jp-vi" ? "JP→VI" : "VI→JP"}
            </h3>
            <p className="text-sm text-red-300 font-mono">
              {"> Ôn lại các từ [KHÓ/QUÊN] để tối ưu hệ thống"}
            </p>

            <div className="bg-black/8rounded-lg p-4">
              <div className="text-xl font-mono font-bold mb-4 text-orange-300">
                {quick4DifficultDirection === "jp-vi" ? quick4DifficultWords[quick4DifficultIndex].word.wordJp : quick4DifficultWords[quick4DifficultIndex].word.wordVi}
              </div>

              {/* Image Display for Difficult Review */}
              {quick4DifficultWords[quick4DifficultIndex].word.imageUrl && (
                <div className="mb-4 bg-black/40 border border-orange-400/30 rounded-xl p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 text-orange-400">
                      <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-mono font-bold text-orange-300">GỢI Ý HÌNH ẢNH ÔN LẠI</span>
                  </div>
                  <div className="flex justify-center">
                    <img
                      src={quick4DifficultWords[quick4DifficultIndex].word.imageUrl}
                      alt={`Gợi ý hình ảnh cho ${quick4DifficultWords[quick4DifficultIndex].word.wordJp}`}
                      className="max-w-full max-h-28 sm:max-h-32 rounded-lg border border-orange-400/40 object-contain cursor-pointer hover:border-orange-400 transition-all duration-300 hover:shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        e.target.nextSibling.style.display = 'none';
                      }}
                      onClick={(e) => {
                        window.open(quick4DifficultWords[quick4DifficultIndex].word.imageUrl, '_blank');
                      }}
                      title="Click to view full size"
                    />
                    <div 
                      className="flex items-center justify-center text-red-300 text-xs font-mono p-3 border border-red-400/50 rounded-lg bg-black/60"
                      style={{ display: 'none' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 mr-2">
                        <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.3 1.3 0 0 1-1.299 2H2.804a1.3 1.3 0 0 1-1.3-2l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                      </svg>
                      Không thể tải hình ảnh
                    </div>
                  </div>
                </div>
              )}

              {!quick4SpellingResult && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={quick4SpellingInput}
                    onChange={(e) => setQuick4SpellingInput(e.target.value)}
                    placeholder={`Nhập bản dịch ${quick4DifficultDirection === "jp-vi" ? "tiếng Việt" : "tiếng Nhật"}`}
                    className="w-full px-4 py-3 rounded-lg bg-black/80 border border-orange-400/50 text-orange-300 text-center text-lg font-mono placeholder-orange-500/50 focus:border-orange-400 focus:shadow-neon-orange transition-all duration-300"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && quick4SpellingInput.trim()) {
                        handleQuick4DifficultSpellingSubmit();
                      }
                    }}
                    autoFocus
                  />
                  <button
                    onClick={handleQuick4DifficultSpellingSubmit}
                    disabled={!quick4SpellingInput.trim()}
                    className="px-6 py-3 bg-black/80 border border-orange-400/50 rounded-lg font-mono font-bold text-orange-300 hover:border-orange-400 hover:shadow-neon-orange-intense hover:text-orange-100 transition-all duration-300 disabled:opacity-50 transform hover:scale-105"
                  >
                    KIỂM TRA ĐÁP ÁN
                  </button>
                </div>
              )}

              {quick4SpellingResult && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border font-mono ${quick4SpellingResult.correct ? "bg-green-900/60 border-green-400/50 text-green-300 shadow-neon-green" : "bg-red-900/60 border-red-400/50 text-red-300 shadow-neon-red"}`}>
                    <div className="font-bold text-lg mb-2">
                      {quick4SpellingResult.correct ? "[THÀNH CÔNG] ĐÚNG!" : "[LỖI] SAI!"}
                    </div>
                    <div className="space-y-1 text-sm">
                      <div>Nhập vào: <span className="font-bold">{quick4SpellingResult.userInput}</span></div>
                      <div>[ĐÁP ÁN]: <span className="font-bold">{quick4SpellingResult.correctAnswer}</span></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-sm text-cyan-300 font-mono">[ĐÁNH GIÁ ÔN LẠI]:</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => handleQuick4DifficultyChoice("again")} className="p-3 bg-black/80 border border-red-400/50 rounded-lg font-mono font-bold text-red-300 hover:border-red-400 hover:shadow-neon-red-intense hover:text-red-100 transition-all duration-300 transform hover:scale-105">
                        QUÊN
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("hard")} className="p-3 bg-black/80 border border-orange-400/50 rounded-lg font-mono font-bold text-orange-300 hover:border-orange-400 hover:shadow-neon-orange-intense hover:text-orange-100 transition-all duration-300 transform hover:scale-105">
                        KHÓ
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("medium")} className="p-3 bg-black/80 border border-blue-400/50 rounded-lg font-mono font-bold text-blue-300 hover:border-blue-400 hover:shadow-neon-blue-intense hover:text-blue-100 transition-all duration-300 transform hover:scale-105">
                        BÌNH THƯỜNG
                      </button>
                      <button onClick={() => handleQuick4DifficultyChoice("easy")} className="p-3 bg-black/80 border border-green-400/50 rounded-lg font-mono font-bold text-green-300 hover:border-green-400 hover:shadow-neon-green-intense hover:text-green-100 transition-all duration-300 transform hover:scale-105">
                        DỄ
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Audio Controls */}
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  const word = quick4DifficultWords[quick4DifficultIndex].word;
                  if (word.audioVocab) {
                    playVocabAudio(word.audioVocab, word.wordJp);
                  } else {
                    playGoogleTTS(word.wordJp);
                  }
                }}
                className="p-3 bg-black/80 border border-cyan-400/50 rounded-lg hover:border-cyan-400 hover:shadow-neon-cyan-intense transition-all duration-300 backdrop-blur-sm text-cyan-300 hover:text-cyan-100 transform hover:scale-105"
                title="[PLAY_VOCAB_AUDIO]"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                </svg>
              </button>

              {quick4DifficultWords[quick4DifficultIndex].word.exampleSentence && (
                <button
                  onClick={() => {
                    const word = quick4DifficultWords[quick4DifficultIndex].word;
                    if (word.audioSentence) {
                      playSentenceAudio(word.audioSentence, word.sentenceStartTime || 0, word.sentenceEndTime || 0);
                    } else {
                      playGoogleTTS(word.exampleSentence);
                    }
                  }}
                  className="p-3 bg-black/80 border border-purple-400/50 rounded-lg hover:border-purple-400 hover:shadow-neon-purple-intense transition-all duration-300 backdrop-blur-sm text-purple-300 hover:text-purple-100 transform hover:scale-105"
                  title="[PLAY_EXAMPLE_AUDIO]"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C13.1 2 14 2.9 14 4V20C14 21.1 13.1 22 12 22C10.9 22 10 21.1 10 20V4C10 2.9 10.9 2 12 2ZM19 10V14C19 15.1 18.1 16 17 16H16V10H17C18.1 10 19 10.9 19 14ZM8 10V16H7C5.9 16 5 15.1 5 14V10C5 10.9 5.9 10 7 10H8Z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Step */}
      {quick4ReviewStep === "summary" && (
        <div className="bg-black/60 backdrop-blur-sm border border-green-400/50 rounded-xl p-6 shadow-neon-green">
          <div className="text-center space-y-4">
            <h3 className="text-xl font-mono font-bold text-green-300">MISSION COMPLETE</h3>

            {/* Statistics */}
            <div className="bg-black/80 border border-green-400/30 rounded-lg p-4 backdrop-blur-sm shadow-neon-green">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-mono font-bold text-cyan-300">{quick4ReviewWords.length}</div>
                  <div className="text-xs text-cyan-400/80 font-mono">UNITS LEARNED</div>
                </div>
                <div>
                  <div className="text-2xl font-mono font-bold text-orange-300">{quick4DifficultWords.length}</div>
                  <div className="text-xs text-orange-400/80 font-mono">HARD/FORGOT</div>
                </div>
                <div>
                  <div className="text-2xl font-mono font-bold text-green-300">
                    {quick4ReviewWords.length * 2 - quick4DifficultWords.length}
                  </div>
                  <div className="text-xs text-green-400/80 font-mono">SUCCESS COUNT</div>
                </div>
              </div>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {quick4ReviewWords.map((word, index) => {
                const result = quick4Results[index];
                return (
                  <div key={word.id} className="bg-black/70 border border-purple-400/30 rounded-lg p-4 backdrop-blur-sm shadow-neon-purple">
                    <div className="font-mono font-bold text-lg mb-2 text-purple-300">
                      {word.wordJp} {"→"} {word.wordVi}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-black/60 border border-cyan-400/30 rounded p-2 flex justify-center items-center gap-2">
                        <div className="text-cyan-400 font-mono">[JP→VI]:</div>
                        <div className={`font-mono font-bold ${result.jpVi === "again" ? "text-red-300" : result.jpVi === "hard" ? "text-orange-300" : result.jpVi === "medium" ? "text-blue-300" : "text-green-300"}`}>
                          {result.jpVi === "again" && "FORGOT"}
                          {result.jpVi === "hard" && "HARD"}
                          {result.jpVi === "medium" && "MEDIUM"}
                          {result.jpVi === "easy" && "EASY"}
                        </div>
                      </div>
                      <div className="bg-black/60 border border-green-400/30 rounded p-2 flex justify-center items-center gap-2">
                        <div className="text-green-400 font-mono">[VI→JP]:</div>
                        <div className={`font-mono font-bold ${result.viJp === "again" ? "text-red-300" : result.viJp === "hard" ? "text-orange-300" : result.viJp === "medium" ? "text-blue-300" : "text-green-300"}`}>
                          {result.viJp === "again" && "FORGOT"}
                          {result.viJp === "hard" && "HARD"}
                          {result.viJp === "medium" && "MEDIUM"}
                          {result.viJp === "easy" && "EASY"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={finishQuick4Review}
                className="px-6 py-3 bg-black/80 border border-green-400/50 rounded-lg font-mono font-bold text-green-300 hover:border-green-400 hover:shadow-neon-green-intense hover:text-green-100 transition-all duration-300 transform hover:scale-105"
              >
                EXIT SESSION
              </button>
              <button
                onClick={startQuick4Review}
                className="px-6 py-3 bg-black/80 border border-cyan-400/50 rounded-lg font-mono font-bold text-cyan-300 hover:border-cyan-400 hover:shadow-neon-cyan-intense hover:text-cyan-100 transition-all duration-300 transform hover:scale-105"
              >
                CONTINUE REVIEW
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end items-center items">
        {quick4ReviewStep !== "summary" && (
          <button
            onClick={() => {
              setQuick4SpellingInput("");
              setQuick4SpellingResult(null);
              if (quick4ReviewStep === "review-difficult") {
                handleQuick4DifficultNext();
              } else {
                handleQuick4Next();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-black/80 border border-yellow-400/50 rounded-lg font-mono font-bold text-yellow-300 hover:border-yellow-400 hover:shadow-neon-yellow hover:text-yellow-100 transition-all duration-300 transform hover:scale-105"
          >
            [SKIP]
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

export default Quick4Review; 